import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useWhisperRecorder } from "@/lib/useWhisperRecorder";

/**
 * DictateButton – reusable microphone button that transcribes German speech
 * via Whisper-1 and appends the result to the existing text via onAppend.
 *
 * Props:
 *   onAppend(text)  – called with the transcribed string. Default behaviour
 *                     should be to APPEND (not replace) to preserve typed text.
 *   size            – "sm" | "md" (default "md")
 *   title           – custom tooltip
 *   language        – Whisper language hint (default "de")
 *   testid          – data-testid prefix
 */
export function DictateButton({ onAppend, size = "md", title, language = "de", testid = "dictate-btn" }) {
  const { recording, transcribing, start, stop, supported } = useWhisperRecorder({
    language,
    onTranscript: (text) => {
      if (text) onAppend?.(text);
    },
    onError: (msg) => toast.error(msg),
  });

  if (!supported) return null;

  const busy = recording || transcribing;
  const sz = size === "sm" ? "h-9 w-9" : "h-11 w-11";
  const iconSize = size === "sm" ? 15 : 18;

  let icon;
  let bg;
  if (transcribing) {
    icon = <Loader2 className="animate-spin" size={iconSize} />;
    bg = "bg-zinc-700 text-zinc-200";
  } else if (recording) {
    icon = <MicOff size={iconSize} />;
    bg = "bg-red-500 text-white animate-pulse";
  } else {
    icon = <Mic size={iconSize} />;
    bg = "bg-zinc-800 hover:bg-zinc-700 text-zinc-300";
  }

  return (
    <button
      type="button"
      onClick={() => (recording ? stop() : start())}
      disabled={transcribing}
      data-testid={testid}
      title={title || (recording ? "Aufnahme stoppen" : "Diktat starten")}
      aria-label={recording ? "Aufnahme stoppen" : "Diktat starten"}
      className={`${sz} rounded-xl ${bg} flex items-center justify-center flex-shrink-0 disabled:opacity-60 active:scale-95 transition`}
    >
      {icon}
      {busy && (
        <span className="sr-only">
          {recording ? "Aufnahme läuft" : "Transkription läuft"}
        </span>
      )}
    </button>
  );
}

export default DictateButton;
